import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from './api'
import { clearSession, establishSession } from './sessionCache'
import { useSessionStore } from '@/store/session'
import type { LoginRequest, RegisterRequest } from '@/types/api'

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: LoginRequest) => authApi.login(request),
    onSuccess: async (data) => {
      await establishSession(queryClient, data.user, data.accessToken)
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: RegisterRequest) => authApi.register(request),
    onSuccess: async (data) => {
      await establishSession(queryClient, data.user, data.accessToken)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => clearSession(queryClient),
  })
}

/**
 * Al cargar la app se perdio el access token (vivia solo en memoria), pero
 * la cookie de refresh puede seguir vigente. Se intenta una renovacion
 * silenciosa una sola vez; si falla, el visitante simplemente queda anonimo.
 */
export function useBootstrapSession() {
  const status = useSessionStore((state) => state.status)
  const queryClient = useQueryClient()
  const attempted = useRef(false)

  useEffect(() => {
    if (status !== 'checking' || attempted.current) return
    attempted.current = true
    authApi
      .refresh()
      .then((data) => establishSession(queryClient, data.user, data.accessToken))
      .catch(() => clearSession(queryClient))
  }, [queryClient, status])
}

